import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import * as AppModule from './App';
import * as ErrorBoundaryModule from './components/ErrorBoundary';
import 'leaflet/dist/leaflet.css';

if (process.env.NODE_ENV === 'production') {
  console.log = () => {};
  console.warn = () => {};
  console.info = () => {};
  console.debug = () => {};
}

function resolveComponent(moduleRef, preferredName, seen = new Set()) {
  if (!moduleRef || seen.has(moduleRef)) return null;
  seen.add(moduleRef);

  if (typeof moduleRef === 'function') return moduleRef;

  if (preferredName && typeof moduleRef[preferredName] === 'function') {
    return moduleRef[preferredName];
  }

  if (moduleRef.default) {
    const resolvedDefault = resolveComponent(moduleRef.default, preferredName, seen);
    if (resolvedDefault) return resolvedDefault;
  }

  for (const key of Object.keys(moduleRef)) {
    const value = moduleRef[key];
    if (typeof value === 'function') return value;
    if (value && typeof value === 'object') {
      const nested = resolveComponent(value, preferredName, seen);
      if (nested) return nested;
    }
  }

  return null;
}

const App = resolveComponent(AppModule, 'App');
const ErrorBoundary = resolveComponent(ErrorBoundaryModule, 'ErrorBoundary');

function isRenderableType(value) {
  if (typeof value === 'function') return true;
  return !!(value && typeof value === 'object' && value.$$typeof);
}

// ─── Service Worker Bootstrap ─────────────────────────────────────────────
// IMPORTANT: We intentionally do NOT register /service-worker.js here anymore.
// We now use a single unified service worker at /firebase-messaging-sw.js
// (registered by App.js) that handles BOTH push notifications AND app-shell
// caching.
//
// Previously two service workers competed for scope "/" — the caching one
// would sometimes win, causing push events to be dropped when the app was
// closed. That's why background notifications didn't work.
//
// This function ONLY runs cleanup: it unregisters any legacy
// /service-worker.js that returning users still have cached. The stub at
// /service-worker.js also self-unregisters on activation as a safety net.
function cleanupLegacyServiceWorker() {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;

  if (process.env.NODE_ENV !== 'production') {
    // In development, unregister EVERYTHING to avoid stale-cache confusion
    window.addEventListener('load', () => {
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        registrations.forEach((registration) => {
          registration.unregister().catch(() => {});
        });
      }).catch(() => {});
    });
    return;
  }

  // Production: only unregister the legacy /service-worker.js — keep
  // /firebase-messaging-sw.js (registered by App.js) active.
  window.addEventListener('load', () => {
    navigator.serviceWorker.getRegistrations().then((registrations) => {
      registrations.forEach((registration) => {
        const scriptURL = registration.active?.scriptURL
                       || registration.installing?.scriptURL
                       || registration.waiting?.scriptURL
                       || '';
        if (scriptURL.endsWith('/service-worker.js')) {
          registration.unregister()
            .then(() => console.log('[bootstrap] Unregistered legacy /service-worker.js'))
            .catch(() => {});
        }
      });
    }).catch(() => {});
  });
}

cleanupLegacyServiceWorker();

const appFallback = (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#fff7ed' }}>
    <div style={{ maxWidth: 760, width: '100%', background: '#fff', border: '1px solid #fdba74', color: '#9a3412', borderRadius: 12, padding: 16, fontSize: 14, fontWeight: 600 }}>
      May startup error sa app. Hard refresh (Ctrl+F5) muna, then subukan ulit.
    </div>
  </div>
);

const startupError = (!App) ? (
  <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16, background: '#fff7ed' }}>
    <div style={{ maxWidth: 760, width: '100%', background: '#fff', border: '1px solid #fdba74', color: '#9a3412', borderRadius: 12, padding: 16, fontSize: 13, lineHeight: 1.5 }}>
      <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 8 }}>Startup error</div>
      <div style={{ fontWeight: 600 }}>
        May component import mismatch sa app bootstrap.
      </div>
      <div style={{ marginTop: 8, fontWeight: 700 }}>
        App: {App ? 'OK' : 'Invalid'}
      </div>
    </div>
  </div>
) : null;

const appTree = isRenderableType(ErrorBoundary)
  ? (
    <ErrorBoundary fallback={appFallback}>
      <App />
    </ErrorBoundary>
  )
  : <App />;

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    {startupError || appTree}
  </React.StrictMode>
);